import React, { forwardRef, useState, useRef, useImperativeHandle } from 'react';
import { Warehouse as WarehouseIcon } from 'lucide-react';
import { useWarehouseCache } from '../context/WarehouseCacheContext';
import { useTranslation } from 'react-i18next';

/**
 * Size variants for WarehouseSelect
 * @default "default" - labeled select with icon, used in main form areas
 * @table - h-6 borderless select for inline table-cell editing (matches CachedUnitMeasurementDropdown)
 */
type WarehouseSelectSize = 'default' | 'table';

interface WarehouseSelectProps {
  value: string | null;
  onChange: (warehouseId: string | null) => void;
  disabled?: boolean;
  required?: boolean;
  hideIcon?: boolean;
  size?: WarehouseSelectSize;
  onKeyDown?: (e: React.KeyboardEvent<HTMLSelectElement>) => void;
}

const WarehouseSelect = forwardRef<HTMLSelectElement, WarehouseSelectProps>(({
  value,
  onChange,
  disabled = false,
  required = false,
  hideIcon = false,
  size = 'default',
  onKeyDown,
}, ref) => {
  const { t } = useTranslation();
  const { getWarehouses, isLoading } = useWarehouseCache();
  const warehouses = getWarehouses();
  const [searchQuery, setSearchQuery] = useState('');
  const selectRef = useRef<HTMLSelectElement>(null);

  useImperativeHandle(ref, () => selectRef.current as HTMLSelectElement);

  // Filter warehouses by search query
  const filteredWarehouses = warehouses.filter(warehouse => {
    const query = searchQuery.toLowerCase();
    return (
      warehouse.code.toLowerCase().includes(query) ||
      warehouse.name.toLowerCase().includes(query)
    );
  });

  if (size === 'table') {
    return (
      <select
        ref={selectRef}
        value={value || ''}
        onChange={(e) => onChange(e.target.value || null)}
        onKeyDown={onKeyDown}
        disabled={disabled || isLoading}
        required={required}
        className="h-6 w-full border-0 bg-transparent px-1 focus:outline-none focus:ring-2 focus:ring-blue-500/50 dark:focus:ring-blue-600/50 focus:bg-blue-50 dark:focus:bg-blue-900/30 disabled:bg-gray-50 disabled:text-gray-500"
      >
        <option value="">&mdash;</option>
        {filteredWarehouses.map((warehouse) => (
          <option key={warehouse._id} value={warehouse._id}>
            {warehouse.code} - {warehouse.name}
          </option>
        ))}
      </select>
    );
  }

  return (
    <div className="relative">
      <div className="flex items-center gap-2">
        {!hideIcon && <WarehouseIcon size={16} className="text-white" />}
        <select
          ref={selectRef}
          value={value || ''}
          onChange={(e) => onChange(e.target.value || null)}
          disabled={disabled || isLoading}
          required={required}
          className="w-full h-10 border border-gray-300 rounded-lg px-3 text-sm focus:ring-2 focus:ring-blue-100 focus:border-blue-500 transition-all duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed"
        >
          <option value="">{t("placeholders.selectWarehouse")}</option>
          {filteredWarehouses.map((warehouse) => (
            <option key={warehouse._id} value={warehouse._id}>
              {warehouse.code} - {warehouse.name}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
});

WarehouseSelect.displayName = 'WarehouseSelect';

export default WarehouseSelect;
